<script setup lang="ts">
import { useLeaderboardStore } from '#leaderboards/store/useLeaderboardStore'
import { isNotFullyIntegrated } from '#leaderboards/utils/integration'
import { formatValue } from '@forgd/formatter'
import { Badge } from '@forgd/ui-kit/badge'
import { Spinner } from '@forgd/ui-kit/spinner'
import { Stat, StatContent, StatGroup, StatHeader, StatLabel, StatValue } from '@forgd/ui-kit/stat'

type PerformanceMetrics = Pick<
  SuccessResponseBody<'/market-makers-leaderboard/performance/{marketMakerId}'>,
  'tradingKpis' | 'trustIntegration' | 'coverageCapabilities' | 'uptime' | 'integrationLevel' | 'integrationStatus' | 'composite'
>

type Rank = {
  value: number | null
  percentile?: number | null
} | null

const props = defineProps<{
  aggregated?: Rank
  depth?: Rank
  volume?: Rank
  spread?: Rank
  kpi?: Rank
  projectsCount?: number
  averageFdv?: number
  loading?: boolean
  performanceMetrics?: PerformanceMetrics | null
}>()

const store = useLeaderboardStore()

const leaderboardBreakdown = computed(() => {
  return [
    { type: 'aggregated' as const, ...props.aggregated },
    { type: 'depth' as const, ...props.depth },
    { type: 'volume' as const, ...props.volume },
    { type: 'spread' as const, ...props.spread },
    { type: 'kpi' as const, ...props.kpi },
  ]
})

const projectsDetails = computed(() => ([
  {
    title: 'Active Engagements in Forgd',
    value: props.projectsCount,
  },
  {
    title: 'Avg. FDV of Projects in Forgd',
    value: formatValue(props.averageFdv, 'currency:0'),
  },
]))

const notFullyIntegrated = computed(() => isNotFullyIntegrated(props.performanceMetrics?.integrationStatus))

const isSectionVisible = computed(() => {
  return leaderboardBreakdown.value.some(item => item.percentile != null || (item.value && item.value > 0))
})

const hasContent = computed(() =>
  !!props.performanceMetrics
  || (!notFullyIntegrated.value && (isSectionVisible.value || (!!props.projectsCount && props.projectsCount > 0))),
)
</script>

<template>
  <div class="relative" :class="loading && !hasContent && 'min-h-20'">
    <section class="flex flex-col gap-4" :class="loading && 'invisible'">
      <div v-if="isSectionVisible && !notFullyIntegrated" class="flex items-center gap-2">
        <h3 class="text-sm font-semibold text-primal">
          Leaderboard Performance
        </h3>
        <Badge>
          {{ store.timeframeLabel }}
        </Badge>
      </div>
      <LPerformanceIndex v-if="performanceMetrics" :data="performanceMetrics" />
      <div v-if="isSectionVisible && !notFullyIntegrated" class="flex flex-col gap-2">
        <LPerformanceCard
          v-for="item in leaderboardBreakdown"
          :key="item.type"
          :type="item.type"
          :percentile="item.percentile"
          :value="item.value"
        />
      </div>

      <StatGroup v-if="!!projectsCount && projectsCount > 0 && !notFullyIntegrated" variant="cards" :columns="2">
        <Stat v-for="item in projectsDetails" :key="item.title" variant="card">
          <StatHeader>
            <StatLabel>{{ item.title }}</StatLabel>
          </StatHeader>
          <StatContent>
            <StatValue>{{ item.value }}</StatValue>
          </StatContent>
        </Stat>
      </StatGroup>
    </section>
    <div v-if="loading" class="absolute inset-0 flex items-center justify-center">
      <Spinner size="md" />
    </div>
  </div>
</template>
